import React, { useState, useEffect } from 'react'
import { Avatar, List, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, MenuList } from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'
import LaptopIcon from '@mui/icons-material/Laptop'
import InsightsIcon from '@mui/icons-material/Insights'
import ImportantDevicesIcon from '@mui/icons-material/ImportantDevices'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu'
import RedeemIcon from '@mui/icons-material/Redeem'
import MultipleStopIcon from '@mui/icons-material/MultipleStop'
import AuthService from '../service'

const ICON_MAP = {
  storefront: StorefrontIcon,
  laptop: LaptopIcon,
  insights: InsightsIcon,
  important_devices: ImportantDevicesIcon,
  support_agent: SupportAgentIcon,
  restaurant_menu: RestaurantMenuIcon,
  redeem: RedeemIcon,
}

export default function MenuSistema({ selected = 'analytics' }) {
  const jwtToken = AuthService.getToken()
  const [anchorEl, setAnchorEl] = useState(null)
  const [sistemas, setSistemas] = useState([])
  const [hosts, setHosts] = useState({})
  const open = Boolean(anchorEl)

  useEffect(() => {
    const loadMenuSistemas = async () => {
      // Em produção sempre do host oficial. Localhost só em dev (com fallback pro oficial).
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      const urls = isDev
        ? ['http://localhost:3005/menu-sistemas.json', 'https://pdv.sischef.com/menu-sistemas.json']
        : ['https://pdv.sischef.com/menu-sistemas.json']

      for (const url of urls) {
        try {
          const res = await fetch(url, { signal: AbortSignal.timeout(5000) })
          if (res.ok) {
            const data = await res.json()
            setSistemas(data.sistemas)
            setHosts(data.host)
            return
          }
        } catch (err) {
          console.warn(`Falha ao carregar de ${url}:`, err.message)
          continue
        }
      }

      // Se chegou aqui, nenhuma URL funcionou
      console.error('Falha ao carregar menu-sistemas.json de todas as fontes')
      setSistemas([])
      setHosts({})
    }

    loadMenuSistemas()
  }, [])

  const handleClickListItem = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const getEnvironment = () => {
    // Detectar ambiente (dev ou production)
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'development'
      : 'production'
  }

  const buildUrl = (sistema) => {
    const hostConfig = hosts[sistema.hostKey]

    if (!hostConfig) {
      console.warn(`Host não configurado para: ${sistema.hostKey}`)
      return '#'
    }

    const host = hostConfig[getEnvironment()]
    if (!host) {
      console.warn(`Host não configurado para ${sistema.hostKey} em ${getEnvironment()}`)
      return '#'
    }

    // Autologin por caminho: {token} é substituído pelo JWT (quando há token)
    if (jwtToken && sistema.tokenPath) {
      return host + sistema.tokenPath.replace('{token}', encodeURIComponent(jwtToken))
    }

    let url = host + (sistema.path || '')

    // Adicionar token como parâmetro se necessário
    if (sistema.tokenParam && jwtToken) {
      const separator = url.includes('?') ? '&' : '?'
      url += `${separator}${sistema.tokenParam}=${encodeURIComponent(jwtToken)}`
    }

    return url
  }

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || StorefrontIcon
    return <IconComponent />
  }

  return (
    <React.Fragment>
      <List
        disablePadding
        aria-label="Sistemas"
        sx={{ width: '100%' }}>
        <ListItemButton
          id="lock-button-sistemas"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClickListItem}>
          <ListItemAvatar>
            <Avatar alt="Sischef Analytics" src={process.env.PUBLIC_URL + "/logo-sischef.svg"} />
          </ListItemAvatar>
          <ListItemText
            primary="Sischef "
            primaryTypographyProps={{
              style: { fontSize: "20px", fontWeight: "500", letterSpacing: "0px", fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' }
            }}
            secondary="Analytics"
            secondaryTypographyProps={{
              style: { marginTop: "-5px" }
            }}
          />
          <MultipleStopIcon />
        </ListItemButton>
      </List>

      <Menu
        keepMounted
        id="lock-button-sistemas"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}>
        <MenuList disablePadding>
          {sistemas.length === 0 ? (
            <MenuItem disabled>
              <ListItemText
                primary="Menu indisponível"
                secondary="Tente novamente em alguns instantes"
                primaryTypographyProps={{ style: { fontSize: '14px' } }}
                secondaryTypographyProps={{ style: { fontSize: '12px' } }}
              />
            </MenuItem>
          ) : (
            sistemas.map((sistema) => {
              const url = buildUrl(sistema)
              const isSelected = sistema.id === selected

              return (
                <MenuItem
                  key={sistema.id}
                  selected={isSelected}
                  component="a"
                  href={url}
                  target={sistema.target}
                  disabled={url === '#'}
                  onClick={() => {
                    if (sistema.target === '_blank') {
                      handleClose()
                    }
                  }}>
                  <ListItemIcon>
                    {getIcon(sistema.icone)}
                  </ListItemIcon>
                  <ListItemText
                    primary={sistema.nome}
                    secondary={sistema.descricao}
                  />
                </MenuItem>
              )
            })
          )}
        </MenuList>
      </Menu>
    </React.Fragment>
  )
}
